import React, { useState, useRef, useEffect } from "react";
import { get } from "lodash";
import { IconChevronLeft } from "@tabler/icons-react";
import { HeatMapPolygon } from "../../models/heatMapModel";
import ZoneHeader from "./ZoneHeader";
import ZonePriceCard from "./ZonePriceCard";
import ZoneMetrics from "./ZoneMetrics";
import ZoneTrends from "./ZoneTrends";
import ZoneActions from "./ZoneActions";
import ZoneEmptyState from "./ZoneEmptyState";

interface Props {
  polygon: HeatMapPolygon | null;
  onClose: () => void;
  navbarHeight?: number;
}

const ZoneDrawer: React.FC<Props> = ({ polygon, onClose, navbarHeight = 0 }) => {
  const [isOpen, setIsOpen] = useState(false);

  // Retiene el último polygon para mostrar contenido durante la animación de cierre
  const lastPolygon = useRef<HeatMapPolygon | null>(null);
  useEffect(() => {
    if (polygon) lastPolygon.current = polygon;
  }, [polygon]);

  // Abre automáticamente cuando llega un polígono
  useEffect(() => {
    if (polygon) setIsOpen(true);
  }, [polygon]);

  const displayed = polygon ?? lastPolygon.current;
  const sales = get(displayed, "display_info.prices[0].sales");
  const avgPrice = get(sales, "mid_m2", "$0");
  const minPrice = get(sales, "min_price", "$0");
  const maxPrice = get(sales, "high_price", "$0");

  const handleClose = () => {
    setIsOpen(false);
    onClose();
  };

  // Swipe: abre arrastrando hacia la izquierda, cierra arrastrando hacia la derecha
  const touchStart = useRef<number | null>(null);
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStart.current = e.touches[0].clientX;
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStart.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStart.current;
    const THRESHOLD = 50;
    if (delta < -THRESHOLD && !isOpen) setIsOpen(true);
    if (delta > THRESHOLD && isOpen) handleClose();
    touchStart.current = null;
  };

  return (
    <div
      className={`fixed right-0 w-full max-w-[400px] z-50 transition-transform duration-300 ease-in-out pointer-events-auto ${isOpen ? "translate-x-0" : "translate-x-full"}`}
      style={{ top: navbarHeight + 52, maxHeight: `calc(100vh - ${navbarHeight + 52}px)` }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Pestaña para abrir/cerrar — visible cuando el drawer está cerrado */}
      <button
        onClick={() => setIsOpen(true)}
        className={`absolute left-0 top-1/2 -translate-x-full -translate-y-1/2 bg-white shadow-lg rounded-l-xl px-1.5 py-4 transition-opacity duration-300 ${isOpen ? "opacity-0 pointer-events-none" : "opacity-100"}`}
      >
        <IconChevronLeft size={20} className="text-gray-600" />
      </button>

      {/* Panel */}
      <div className="bg-white rounded-l-2xl shadow-2xl">
        <div className="px-4 pt-4 h-[60px]">
          <ZoneHeader name={displayed?.name ?? ""} onClose={handleClose} />
        </div>
        {displayed ? (
          <>
            <div className="flex-1 overflow-y-auto px-4" style={{ maxHeight: `calc(100vh - ${navbarHeight + 52}px - (180px + 48px + 24px))` }}>
              <ZonePriceCard avgPrice={avgPrice} minPrice={minPrice} maxPrice={maxPrice} />
              <ZoneMetrics />
              <ZoneTrends key={`${displayed.id}-${isOpen}`} />
            </div>
            <div className="px-4 py-3 min-h-[120px]">
              <ZoneActions zoneName={displayed.name} />
            </div>
          </>
        ) : (
          <ZoneEmptyState />
        )}
      </div>
    </div>
  );
};

export default ZoneDrawer;
